import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getIncidentReports } from "../../lib/api";
import { printPage } from "../../lib/print";
import { useAuth } from "../../App";
import ReportAttachments from "./ReportAttachments";

const INCIDENT_TYPES = [
  "Not Ready / Refusal to participate",
  "Disruption of learning",
  "Left Building",
  "Threatening language-verbal",
  "Threatening language-written",
  "Property damage",
  "Physical Altercation",
  "Sexual Harassment",
  "Threats of self-harm",
  "Self-harm",
  "Disclosure of abuse or neglect",
  "Home Life Issue",
  "Contraband Found",
  "Substance Abuse",
];

// Academic year runs Aug 1 → Jul 31.
const schoolYearStart = (dateStr) => {
  if (!dateStr || dateStr.length < 7) return null;
  const y = parseInt(dateStr.slice(0, 4), 10);
  const m = parseInt(dateStr.slice(5, 7), 10);
  if (!y || !m) return null;
  return m >= 8 ? y : y - 1;
};
const schoolYearLabel = (startYear) => `${startYear}\u2013${startYear + 1}`;
const currentSchoolYearStart = () => schoolYearStart(new Date().toISOString().slice(0, 10));

const studentName = (s) =>
  `${s.preferred_name || s.first_name || ""} ${s.last_name || ""}`.trim();

export default function IncidentReportsTab({ students = [], formerStudents = [] }) {
  const { isAdmin: isAdminFn, hasRole } = useAuth();
  const isAdmin = isAdminFn();
  const isClinician = hasRole("Clinician");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schoolYear, setSchoolYear] = useState(() => String(currentSchoolYearStart()));
  const [sortOrder, setSortOrder] = useState("newest");
  const [studentFilter, setStudentFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const roster = useMemo(
    () => [...(students || []), ...(formerStudents || [])],
    [students, formerStudents]
  );
  const nameMap = useMemo(() => {
    const m = {};
    roster.forEach((s) => { if (s.id) m[s.id] = studentName(s); });
    return m;
  }, [roster]);
  const studentOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    roster.forEach((s) => {
      if (s.id && !seen.has(s.id)) { seen.add(s.id); opts.push({ id: s.id, name: studentName(s) }); }
    });
    return opts.sort((a, b) => a.name.localeCompare(b.name));
  }, [roster]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getIncidentReports({ limit: 1000 });
        // Whole-school incident & clinical reports only (exclude general notes &
        // any accident-type rows). Visibility is already enforced server-side.
        const list = (res.data || []).filter((r) =>
          ["incident", "clinical"].includes((r.type || "").toLowerCase())
        );
        setReports(list);
      } catch {
        toast.error("Failed to load incident reports");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const availableYears = useMemo(() => {
    const set = new Set();
    reports.forEach((r) => { const sy = schoolYearStart(r.date); if (sy != null) set.add(sy); });
    return Array.from(set).sort((a, b) => b - a);
  }, [reports]);

  const filtered = useMemo(() => {
    const out = reports.filter((r) => {
      if (schoolYear !== "all" && String(schoolYearStart(r.date)) !== String(schoolYear)) return false;
      if (studentFilter !== "all" && r.student_id !== studentFilter) return false;
      if (typeFilter !== "all") {
        const t = (r.type || "").toLowerCase();
        if (typeFilter === "clinical") { if (t !== "clinical") return false; }
        else {
          // a specific incident category
          if (t !== "incident" || (r.incident_type || "") !== typeFilter) return false;
        }
      }
      return true;
    });
    return out.sort((a, b) => {
      const da = a.date || "", dbb = b.date || "";
      return sortOrder === "newest" ? dbb.localeCompare(da) : da.localeCompare(dbb);
    });
  }, [reports, schoolYear, studentFilter, typeFilter, sortOrder]);

  const yearText = schoolYear === "all" ? "any year" : schoolYearLabel(parseInt(schoolYear, 10));

  const handlePrint = (r) => {
    const name = nameMap[r.student_id] || "Unknown Student";
    const typeLabel = r.type === "clinical" ? "Clinical Report" : "Incident Report";
    const content = `
      <div style="text-align:center;margin-bottom:16px;">
        <span class="badge">${typeLabel}${r.incident_type ? ` — ${r.incident_type}` : ""}</span>
      </div>
      <div class="grid-2">
        <div><div class="field-label">Student</div><div class="field-value">${name}</div></div>
        <div><div class="field-label">Date</div><div class="field-value">${r.date || "Not specified"}</div></div>
        <div><div class="field-label">Time</div><div class="field-value">${r.time || "Not specified"}</div></div>
        <div><div class="field-label">Visibility</div><div class="field-value">${(r.visibility || ["Admin"]).join(", ")}</div></div>
      </div>
      <div class="section"><div class="section-title">Description</div><div class="section-content">${r.description || "No description provided"}</div></div>
      ${r.action_taken ? `<div class="section"><div class="section-title">Action Taken</div><div class="section-content">${r.action_taken}</div></div>` : ""}
      ${r.witnesses ? `<div class="section"><div class="section-title">Staff Witness</div><div class="section-content">${r.witnesses}</div></div>` : ""}
    `;
    printPage({
      title: typeLabel,
      subtitle: "SMARTIS — Incident Reports",
      content,
      extraStyles: `body { max-width: 800px; margin: 0 auto; padding: 40px; }`,
    });
  };

  return (
    <Card data-testid="incident-reports-tab">
      <CardHeader>
        <CardTitle>Incident Reports</CardTitle>
        <CardDescription>All incident &amp; clinical reports across the school. Use the filters to scope by school year, student or type.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3" data-testid="incident-reports-toolbar">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">School Year</Label>
            <Select value={schoolYear} onValueChange={setSchoolYear}>
              <SelectTrigger className="w-[150px] h-9" data-testid="incident-reports-year-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {availableYears.map((y) => (<SelectItem key={y} value={String(y)}>{schoolYearLabel(y)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Student</Label>
            <Select value={studentFilter} onValueChange={setStudentFilter}>
              <SelectTrigger className="w-[190px] h-9" data-testid="incident-reports-student-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All students</SelectItem>
                {studentOptions.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[220px] h-9" data-testid="incident-reports-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="clinical">Clinical Report</SelectItem>
                {INCIDENT_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Sort</Label>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[150px] h-9" data-testid="incident-reports-sort-order"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-sm text-muted-foreground" data-testid="incident-reports-count">
          Showing {filtered.length} report{filtered.length === 1 ? "" : "s"} for {yearText}
        </p>

        {loading ? (
          <div className="py-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground text-center py-8" data-testid="incident-reports-empty">No incident reports for {yearText}</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((r, i) => (
              <div key={r.id || i} className="p-4 rounded-lg border" data-testid={`incident-report-row-${i}`}>
                <div className="flex justify-between items-start mb-2 gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${r.type === "clinical" ? "bg-purple-100 text-purple-800" : "bg-gray-100 text-gray-800"}`}>
                      {r.type}{r.incident_type ? ` — ${r.incident_type}` : ""}
                    </span>
                    <span className="font-medium text-sm text-[#00305E]">{nameMap[r.student_id] || "Unknown Student"}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm text-muted-foreground">{r.date}</span>
                    <Button variant="ghost" size="sm" onClick={() => handlePrint(r)} title="Print report" data-testid={`incident-report-print-${i}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/>
                      </svg>
                    </Button>
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap">{r.description}</p>
                {r.action_taken && <p className="text-xs text-muted-foreground mt-2"><span className="font-medium">Action taken:</span> {r.action_taken}</p>}
                {(r.visibility || []).length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-2">Visibility: {(r.visibility || ["Admin"]).join(", ")}</p>
                )}
                <div className="mt-3 border-t pt-3">
                  <ReportAttachments reportType={r.type} reportId={r.id} isAdmin={isAdmin} canManage={isAdmin || isClinician} compact />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
